/* =============================================================================
   CORE / ORDER-SOURCES — Single-Source-of-Truth für Channel + Partner-Routing
   =============================================================================
   Session 59 / Phase 48 (Block C)
   ----------------------------------------------------------------------------
   Vor S59 hatte orders.source ein Sammelfeld mit 6 Werten ('online','phone',
   'walk_in','pos','admin','reservation') — ohne klare Trennung zwischen
   „über welchen Funnel kam die Bestellung rein" und „welcher Partner war
   beteiligt".

   Phase 48 löst das in zwei Achsen auf:

     source_channel ∈ {'cockpit','web','marketplace'}   ← Funnel-Typ
     source_partner ∈ {NULL,'uber_eats','lieferando'}   ← Partner-Quelle

   Cross-Field-Constraint in der DB:
     source_partner gesetzt   ⇔   source_channel='marketplace'

   Dieses Modul exportiert die Konstanten + Helper. Wer eine Order-Source
   prüft, importiert hier statt magic strings.
============================================================================ */

import { isDelivery } from './service-channels.js';

// ---------------------------------------------------------------------------
// Channel-Konstanten (= DB CHECK-Constraint-Werte für orders.source_channel)
// ---------------------------------------------------------------------------
export const SOURCE_CHANNEL_COCKPIT     = 'cockpit';
export const SOURCE_CHANNEL_WEB         = 'web';
export const SOURCE_CHANNEL_MARKETPLACE = 'marketplace';

export const SOURCE_CHANNELS = Object.freeze([
  SOURCE_CHANNEL_COCKPIT,
  SOURCE_CHANNEL_WEB,
  SOURCE_CHANNEL_MARKETPLACE
]);

const SOURCE_CHANNELS_SET = new Set(SOURCE_CHANNELS);

// ---------------------------------------------------------------------------
// Partner-Konstanten (= DB CHECK für orders.source_partner)
// NULL bedeutet „kein Partner" (nur bei cockpit/web).
// ---------------------------------------------------------------------------
export const SOURCE_PARTNER_UBER_EATS  = 'uber_eats';
export const SOURCE_PARTNER_LIEFERANDO = 'lieferando';

export const SOURCE_PARTNERS = Object.freeze([
  SOURCE_PARTNER_UBER_EATS,
  SOURCE_PARTNER_LIEFERANDO
]);

const SOURCE_PARTNERS_SET = new Set(SOURCE_PARTNERS);

// ---------------------------------------------------------------------------
// Labels für UI
// ---------------------------------------------------------------------------
export const SOURCE_CHANNEL_LABELS = Object.freeze({
  [SOURCE_CHANNEL_COCKPIT]:     'Cockpit',
  [SOURCE_CHANNEL_WEB]:         'Eigene Website',
  [SOURCE_CHANNEL_MARKETPLACE]: 'Marketplace'
});

export const SOURCE_PARTNER_LABELS = Object.freeze({
  [SOURCE_PARTNER_UBER_EATS]:  'Uber Eats',
  [SOURCE_PARTNER_LIEFERANDO]: 'Lieferando'
});

export const DEFAULT_SOURCE_CHANNEL = SOURCE_CHANNEL_COCKPIT;

// ---------------------------------------------------------------------------
// Helper-Funktionen
// ---------------------------------------------------------------------------

function _channelOf(input) {
  if (typeof input === 'string') return input.trim();
  if (!input) return '';
  return input.sourceChannel || input.source_channel || '';
}

function _partnerOf(input) {
  if (typeof input === 'string') return input.trim();
  if (!input) return '';
  return input.sourcePartner || input.source_partner || '';
}

export function isValidSourceChannel(value) {
  return SOURCE_CHANNELS_SET.has(_channelOf(value));
}

export function isValidSourcePartner(value) {
  const v = _partnerOf(value);
  return v === '' || SOURCE_PARTNERS_SET.has(v);
}

export function isCockpitOrder(input)     { return _channelOf(input) === SOURCE_CHANNEL_COCKPIT; }
export function isWebOrder(input)         { return _channelOf(input) === SOURCE_CHANNEL_WEB; }
export function isMarketplaceOrder(input) { return _channelOf(input) === SOURCE_CHANNEL_MARKETPLACE; }

/**
 * REF-7a (Phase 73): Walk-In-Heuristik für Orders.
 *
 * Mail-Konzept §4 listet Walk-In als eigenen Pfad ohne Mail-Kontext. Walk-Ins
 * leben primär in `table_occupancies` und haben keine `customer_email`. Wenn
 * trotzdem eine Order am Cockpit angelegt wird (Customer sitzt am Tisch,
 * Operator nimmt Bestellung auf), entsteht ein Cockpit-Order-Eintrag mit
 * `service_type='dine-in'` und ohne `reservation_id` — das ist die Walk-In-
 * Order. Heuristik: dine-in + cockpit + keine Reservation.
 *
 * Liefert false für klassische Cockpit-Takeaway/Delivery und für Web-
 * Reservations mit Pre-Order (die haben eine reservation_id).
 *
 * Service-Type-Lookup akzeptiert camelCase und snake_case (DB vs. JS-Mapper).
 */
/**
 * S244 Cut B — der Kanal-Filter ist RAUS.
 *
 * Bis hierher hiess Walk-In „dine-in + cockpit + keine Reservierung". Seit dem
 * Tisch-QR entsteht dieselbe Situation auch mit `source_channel='web'`: Gäste
 * sitzen am Tisch und bestellen selbst. Fachlich ist das ein Walk-In — sie sind
 * ohne Reservierung da. Mit dem alten Filter wäre so eine Bestellung aus der
 * Kanal-Zählung gefallen (Dashboard-Triple, Auswertung) und aus dem
 * Mail-Ausschluss, obwohl beides für sie gilt.
 *
 * Was „Walk-In" ausmacht, ist der ABLAUF (vor Ort, ohne Voranmeldung), nicht
 * das Gerät, auf dem getippt wurde.
 */
export function isWalkInOrder(input) {
  if (!input || typeof input !== 'object') return false;
  const serviceType = input.serviceType || input.service_type;
  if (serviceType !== 'dine-in') return false;
  // Marketplace bleibt aussen vor: eine andere Welt mit eigenen Regeln (kein
  // Mail-Kontakt, eigene Abrechnung). Ein `dine-in` von dort kann es fachlich
  // nicht geben — falls doch, ist es kein Walk-In, sondern ein Datenfehler.
  if (isMarketplaceOrder(input)) return false;
  const resId = input.reservationId ?? input.reservation_id;
  return !resId;
}

/**
 * S244 Cut B — hat der Gast diese Bestellung selbst am Tisch aufgegeben?
 *
 * `dine-in` + `source_channel='web'` gibt es nur über den Tisch-QR: der
 * Cockpit-Pfad schreibt 'cockpit', Marketplace schreibt 'marketplace', und
 * `create_customer_order` (Takeaway/Lieferung) kann gar kein 'dine-in'.
 * Steuert das Board-Badge „📱 Tisch" und die Toast-Meldung.
 */
export function isTableSelfOrder(input) {
  if (!input || typeof input !== 'object') return false;
  const serviceType = input.serviceType || input.service_type;
  if (serviceType !== 'dine-in') return false;
  return isWebOrder(input);
}

/**
 * S122 — Dashboard-Zähl-Kanal einer Order (Single-Source für die KPI-Kacheln
 * „Heute"/„Offen"):
 *   'delivery'    → service_type='delivery' (zählt nur in die Lieferungs-Spalte)
 *   'reservation' → reservation_id gesetzt (zählt nur über die Reservierungs-
 *                   Spalte; operativ läuft die Order weiter via „Als Nächstes"/KDS/Theke)
 *   'order'       → Walk-In (dine-in ohne Reservierung) + Takeaway = die echten
 *                   „Bestellungen"
 *
 * Pure, liest camel + snake defensiv. Delivery gewinnt vor reservation (Delivery
 * hat nie eine reservation_id, die Reihenfolge ist nur defensiv). null/Non-Object
 * → 'order'.
 */
export function orderCountChannel(order) {
  if (!order || typeof order !== 'object') return 'order';
  if (isDelivery(order)) return 'delivery';
  const resId = order.reservationId ?? order.reservation_id;
  if (resId) return 'reservation';
  return 'order';
}

export function sourceChannelLabel(input) {
  return SOURCE_CHANNEL_LABELS[_channelOf(input)] || '—';
}

export function sourcePartnerLabel(input) {
  const v = _partnerOf(input);
  if (!v) return '';
  return SOURCE_PARTNER_LABELS[v] || v;
}

/**
 * Kombiniertes Label für UI-Listings: „Cockpit", „Eigene Website",
 * „Marketplace · Uber Eats", „Marketplace · Lieferando".
 */
export function sourceDisplayLabel(input) {
  const channel = _channelOf(input);
  const partner = _partnerOf(input);
  const cLabel = SOURCE_CHANNEL_LABELS[channel];
  if (!cLabel) return '—';
  if (channel === SOURCE_CHANNEL_MARKETPLACE && partner) {
    return `${cLabel} · ${SOURCE_PARTNER_LABELS[partner] || partner}`;
  }
  return cLabel;
}

/**
 * Sanitizer: liefert {sourceChannel, sourcePartner} mit Cross-Field-
 * Konsistenz (DB-CHECK-konform). Bei ungültigem Input → Default cockpit.
 */
export function coerceSourceFields(input) {
  let channel = _channelOf(input);
  let partner = _partnerOf(input);
  if (!SOURCE_CHANNELS_SET.has(channel)) channel = DEFAULT_SOURCE_CHANNEL;
  if (channel !== SOURCE_CHANNEL_MARKETPLACE) {
    // Partner darf NUR bei marketplace gesetzt sein (sonst CHECK-Violation).
    partner = null;
  } else if (!SOURCE_PARTNERS_SET.has(partner)) {
    // marketplace ohne gültigen Partner → ungültig, fallback auf cockpit
    channel = DEFAULT_SOURCE_CHANNEL;
    partner = null;
  }
  return { sourceChannel: channel, sourcePartner: partner };
}

/**
 * Backward-Compat-Mapper: alte orders.source-Werte → neue source_channel.
 * Wird vom Frontend genutzt, falls Legacy-Records ohne source_channel
 * geladen werden (sollte nach Backfill nicht mehr passieren).
 */
export function legacySourceToChannel(legacy) {
  switch (legacy) {
    case 'online':      return SOURCE_CHANNEL_WEB;
    case 'reservation': return SOURCE_CHANNEL_WEB;
    case 'admin':
    case 'walk_in':
    case 'pos':
    case 'phone':       return SOURCE_CHANNEL_COCKPIT;
    default:            return DEFAULT_SOURCE_CHANNEL;
  }
}

// ---------------------------------------------------------------------------
// window-Export für inline-Handler.
// ---------------------------------------------------------------------------
if (typeof window !== 'undefined') {
  window.OrderSources = {
    SOURCE_CHANNEL_COCKPIT,
    SOURCE_CHANNEL_WEB,
    SOURCE_CHANNEL_MARKETPLACE,
    SOURCE_CHANNELS,
    SOURCE_PARTNER_UBER_EATS,
    SOURCE_PARTNER_LIEFERANDO,
    SOURCE_PARTNERS,
    SOURCE_CHANNEL_LABELS,
    SOURCE_PARTNER_LABELS,
    DEFAULT_SOURCE_CHANNEL,
    isValidSourceChannel,
    isValidSourcePartner,
    isCockpitOrder,
    isWebOrder,
    isMarketplaceOrder,
    isWalkInOrder,
    isTableSelfOrder,
    orderCountChannel,
    sourceChannelLabel,
    sourcePartnerLabel,
    sourceDisplayLabel,
    coerceSourceFields,
    legacySourceToChannel
  };
}
